<template>
  <div class="list-view vendor-tab">
    <div v-if="tableColOpts">
      <TableGutter
        :tools="tableColOpts.gutterOpts"
        :selectedAction="false"
      />
      <div class="column" v-if="isDeleteMode" >
        <div class="columns">
          <div class="column is-flex is-align-items-center has-background-grey-lighter">
          <button
            class="button has-text-weight-bold has-background-black-bis has-text-white"
            :disabled="!selectedRows.length"
            @click="bulkArchive"
          >
            Delete ({{ selectedRows.length }})
          </button>
          <button
            class="button is-outlined has-text-weight-bold ml-2"
            @click="cancelDeleteMode"
          >
            Cancel
          </button>
          </div>
        </div>
      </div>
      <table-toolbar
        v-else
        :searchPlaceHolder="tableColOpts.toolBar.searchPlaceHolder"
        :toolbarOpts="tableColOpts.toolBar"
        @handleAction="handleAction"
      />
      <data-table
        :key="tableKey"
        :apiMode="true"
        :tableOpts="tableColOpts"
        :loadData="loadData"
        @cell-clicked="onCellClicked"
        @checkbox-toggled="onCheckboxToggled"
      />
    </div>
    <div v-if="isCheckable" class="is-flex is-align-items-center px-3 py-2">
      <button class="button is-small ml-2" @click="toggleCheckable">
        Cancel
      </button>
    </div>
    <vendors-form
      v-if="showAddVendorModal"
      :active="showAddVendorModal"
      :vendor="vendorToEdit"
      @close="closeAddVendorModal"
      @refresh-table="refreshTable"
    />
    <excel-import
      :isExcelModalActive="showExcelModal"
      :customExcelCols="vendorExcelCols"
      :type="'vendors'"
      :batchSize="200"
      importModule="vendor-import"
      :uniqueFields="['name']"
      :validateExcelItem="validateVendor"
      :mapData="mapData"
      :saveExcelItem="saveExcelItem"
      modalHeaderTitle="Import Vendors from Excel"
      @close-modal="showExcelModal = false"
      @refresh-table="refreshTable"
    />
    <activity-slider
      :isActive="isActivityActive"
      :id="companyId"
      module="Company"
      :company="companyId"
      @update:isActive="isActivityActive = false"
    />
  </div>
</template>

<script>
import { onBeforeMount, reactive, toRefs, computed, } from 'vue';
import { useStore } from 'vuex';
import { get } from 'lodash';
import { useToast } from 'vue-toastification';
import { DialogProgrammatic } from '@/components/Dialog';
import DataTable from '../components/table-fields/DataTable.vue';
import TableToolbar from '../components/table-fields/TableToolbar.vue';
import TableGutter from '../components/TableGutter.vue';
import VendorsForm from '../components/modals/VendorsForm.vue';
import vendorListCols from '../components/table-cols/vendorListCols.js';
import Vendors from '../models/Vendors';
import ExcelImport from '@/components/ExcelImport.vue';
import vendorExcelCols from '../components/excel-cols/vendorExcelCols.js';
import ActivitySlider from '@/components/ActivitySlider.vue';

export default {
  name: 'Vendors',
  components: {
    DataTable,
    TableToolbar,
    TableGutter,
    VendorsForm,
    ExcelImport,
    ActivitySlider,
  },
  setup() {
    const store = useStore();
    const toast = useToast();

    const data = reactive({
      tableKey: 0,
      isLoading: false,
      showAddVendorModal: false,
      vendorToEdit: {},
      searchText: '',
      selectedRows: [],
      isCheckable: false,
      showExcelModal: false,
      isDeleteMode: false,
      indexedCategories: [],  
      existingVendorNames: [],
      isActivityActive: false,
    });
    const tableColOpts = computed(() => ({
      ...vendorListCols,
      isCheckable: data.isCheckable,
      fields: vendorListCols.fields.map((f) => {
        if (f.id !== 'action' || !data.isDeleteMode) return f;
        return {
          ...f,
          inputProps: {
            ...f.inputProps,
            groupButtons: (row) => f.inputProps.groupButtons(row).filter((b) => b.event !== 'vendor-archive'),
          },
        };
      }),
    }));
    const companyId = computed(() => store.state.userData?.company || '');
    const refreshTable = () => {
      data.tableKey += 1;
    };

    const loadData = async (commonParam) => {
      const params = {
        ...commonParam,
        sort: {
          sortField: commonParam.sort[0].sortField,
          direction: commonParam.sort[0].direction,
        },
        showItems: 'show-active-catalogs',
        search: data.searchText,
      };
      const result = await Vendors.getVendors(params);
      return {
        data: result.data ?? [],
        total: result.total ?? 0,
      };
    };

    const closeAddVendorModal = () => {
      data.showAddVendorModal = false;
      data.vendorToEdit = {};
    };

    const toggleCheckable = () => {
      data.isCheckable = !data.isCheckable;
      data.selectedRows = [];
    };
    const onCheckboxToggled = (rows) => {
      data.selectedRows = rows;
    };

    const validateVendor = (vendor) => {
      const indexedCategories = _.keyBy(data.indexedCategories, 'uniqueKey');
      vendor.address = [];
      const vaddress = _.pick(vendor, ['location', 'street', 'city', 'state', 'zip', 'phone', 'contactName', 'contactEmail']);
      vendor.address.push(vaddress);
      vendor.categories = _.isUndefined(vendor.categories) ? [] : String(vendor.categories).split(',');
      vendor.subCategories = _.isUndefined(vendor.subCategories) ? [] : String(vendor.subCategories).split(',');
      let errMsg = null;
      vendor.name = String(vendor?.name || '').trim();
      if (vendor.name.length < 3) {
        errMsg = 'Vendor name should be at least 3 characters long';
      } else if (data.existingVendorNames.includes(vendor.name.toUpperCase())) {
        errMsg = 'this name already exists and vendor name should case insensitive';
      } else if (!_.isEmpty(vendor.categories)) {
        vendor.category = [];
        for (const c of vendor.categories) {
          const catName = c.toUpperCase().trim();
          if (_.keys(indexedCategories).includes(catName)) {
            vendor.category.push(indexedCategories[catName]);
          }
        }
        vendor.category = _.uniqBy(vendor.category, '_id');
        if (_.isEmpty(vendor.category)) errMsg = 'Please enter categories present in Company level';
      }
      return errMsg;
    };

    const mapData = async (vendors) => {
      let categories = [];
      const existingVendors = (await Vendors.getVendors({})).data || [];
      data.existingVendorNames = existingVendors
        .map((vendor) => String(vendor?.name || '').trim().toUpperCase())
      _.forEach(vendors, (d) => {
        if (d.categories !== undefined && d.categories !== null && d.categories !== '') {
          categories.push(String(d.categories).split(','));
        }
      });
      categories = _.compact(_.flatMap(categories)).map((c) => c.toUpperCase().trim());
      if (categories.length > 0) {
        let indexedCategories = (await Vendors.getCategories({ category: categories, limit: 1000 })).data || [];
        let allSubCategories = (await Vendors.getCategories({
          categoryId: _.map(indexedCategories, '_id'),
          isParent: false,
        })).data || [];
        allSubCategories = _.groupBy(allSubCategories, 'parent._id');
        for (const c of indexedCategories) {
          c.subCategory = allSubCategories[c._id];
        }
        data.indexedCategories = indexedCategories;
      }
      return vendors;
    };

    const saveExcelItem = async (obj, fileName, isFinalBatch) => {
      let result;
      for (const vendor of obj) vendor.isImported = true;
      if (obj.length > 0) result = await Vendors.addVendor(obj);
      if (isFinalBatch) refreshTable();
      return result;
    };

    const cancelDeleteMode = () => {
      data.isDeleteMode = false;
      data.isCheckable = false;
      data.selectedRows = [];
      refreshTable();
    };

    const bulkArchive = () => {
      const idsToDelete = data.selectedRows.filter((r) => !r.isDefault).map((r) => r._id);
      if (!idsToDelete.length) return;
      DialogProgrammatic.confirm({
        title: 'Delete Vendors',
        message: `Are you sure you want to delete ${idsToDelete.length} vendor(s)?`,
        okButton: 'Delete',
        cancelButton: 'Cancel',
        type: 'danger',
        onConfirm: async () => {
          try {
            const response = await Vendors.archive({ vendorIds: idsToDelete });
            if (response?.warn) {
              toast.warning(response.message);
              return;
            }
            toast.success('Vendors deleted successfully');
            data.selectedRows = [];
            data.isCheckable = false;
            data.isDeleteMode = false;
            refreshTable();
          } catch (e) {
            toast.error(get(e, 'data.message') || 'Error deleting vendors');
          }
        }
      });
    };
    // single ku
    const onCellClicked = ({ type, data: rowData }) => {
      if (type === 'edit') {
        data.vendorToEdit = rowData;
        data.showAddVendorModal = true;
      } else if (type === 'vendor-archive') {
        DialogProgrammatic.confirm({
          title: 'Delete Vendor',
          message: `Are you sure you want to delete "${rowData.name}"?`,
          okButton: 'Delete',
          cancelButton: 'Cancel',
          type: 'danger',
          onConfirm: async () => {
            try {
              const response = await Vendors.archive({
                vendorIds: [rowData._id],
              });
              if (response?.warn) {
                toast.warning(response.message);
                return;
              }
              toast.success('Vendor deleted successfully');
              refreshTable();
            } catch (e) {
              toast.error(get(e, 'data.message') || 'Error deleting vendor');
            }
          }
        });
      }
    };

    const searchData = (val) => {
      if ((!_.isEmpty(data.searchText) && _.isEmpty(val)) || (!_.isEmpty(val) && data.searchText !== val)) {
        data.searchText = val;
        refreshTable();
      }
    };
    const handleAction = async (action, payload) => {
      const actionType = action?.event ?? action?.type ?? action;
      const actionMapper = {
        addNew: () => {
          data.vendorToEdit = {};
          data.showAddVendorModal = true;
        },
        removeVendors: () => {
          data.isCheckable = true;
          data.isDeleteMode = true;
        },
        excelImport: () => { data.showExcelModal = true; },
        openActivity: () => { data.isActivityActive = true; },
        edit: () => {
          data.vendorToEdit = payload ?? {};
          data.showAddVendorModal = true;
        },
        search: (val) => searchData(val),
      };

      try {
        await actionMapper[actionType]?.(action?.data ?? payload);
      } catch (e) {
        toast.error(get(e, 'data.message') || 'Vendor action error');
      }
    };

    onBeforeMount(async () => {
      await store.getters.userPromise;
    });

    return {
      ...toRefs(data),
      tableColOpts,
      toggleCheckable,
      bulkArchive,
      cancelDeleteMode,
      loadData,
      handleAction,
      onCellClicked,
      refreshTable,
      closeAddVendorModal,
      onCheckboxToggled,
      validateVendor,
      mapData,
      saveExcelItem,
      vendorExcelCols,
      companyId,
    };
  },
};
</script>